/** @jsxImportSource react */
import React, { useEffect, useState } from 'react'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useTranslation } from '../i18n'
import { LanguageSelector } from './LanguageSelector'
import { Link, useLocation } from 'react-router-dom'
import { Logo } from './Logo'

export function Navbar({
  scrollToSection,
}: {
  scrollToSection: (id: string) => void
}) {
  const { t } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const isLandingPage = location.pathname === '/'

  // Track scroll position to change navbar style when scrolled
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50)
    }

    // Set initial state
    handleScroll()

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const isNavTransparent = isLandingPage && !scrolled && !isMenuOpen;
  const isTextWhite = (isLandingPage && !scrolled) || isMenuOpen;

  const navItems = [
    {
      id: 'process',
      label: t('howItWorks'),
    },
    {
      id: 'global-hubs',
      label: t('whereWeWork'),
    },
    {
      id: 'why-us',
      label: t('whyUs'),
    },
    {
      id: 'about',
      label: t('aboutUsLink'),
    },
  ]
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isMenuOpen
          ? 'bg-black py-3'
          : isNavTransparent
          ? 'bg-transparent backdrop-blur-sm py-4'
          : 'bg-white shadow-md py-3'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              onClick={() => {
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center"
            >
              <Logo variant="header" />
            </Link>
          </div>
          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center space-x-8 rtl:space-x-reverse">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`transition-colors duration-300 text-sm font-medium relative group ${isTextWhite ? 'text-white' : 'text-gray-700'}`}
              >
                {item.label}
                <span
                  className={`absolute bottom-0 left-0 w-0 h-0.5 ${isTextWhite ? 'bg-white' : 'bg-gray-800'} group-hover:w-full transition-all duration-300`}
                ></span>
              </button>
            ))}
          </div>
          {/* Right Side - Language & Auth */}
          <div className="hidden md:flex items-center space-x-5 rtl:space-x-reverse">
            <div
              className={`transition-colors duration-300 ${isTextWhite ? 'text-white' : 'text-gray-700'}`}
            >
              <LanguageSelector />
            </div>
            <div
              className={`h-5 w-px mx-1 transition-colors ${isTextWhite ? 'bg-gray-200/50' : 'bg-gray-200'}`}
            ></div>
            <Link
              to="/login"
              className={`transition-colors duration-300 text-sm font-medium ${isTextWhite ? 'text-white hover:text-gray-200' : 'text-gray-700 hover:text-gray-900'}`}
            >
              {t('login')}
            </Link>
            <Link
              to="/signup"
              className={`px-5 py-2.5 rounded-lg transition-all duration-300 text-sm font-medium hover:shadow-lg ${
                isNavTransparent
                  ? 'bg-white hover:bg-gray-100 text-gray-900'
                  : 'bg-primary-500 hover:bg-primary-600 text-white'
              }`}
            >
              {t('signup')}
            </Link>
          </div>
          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4 rtl:space-x-reverse">
            <div
              className={`transition-colors duration-300 ${isTextWhite ? 'text-white' : 'text-gray-700'}`}
            >
              <LanguageSelector />
            </div>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-lg transition-colors duration-300 ${isTextWhite ? 'text-white hover:text-gray-200 hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'}`}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu - Fullscreen overlay */}
      <div
        className={`fixed inset-0 bg-black z-50 transition-all duration-300 md:hidden ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="container mx-auto px-6 py-8 h-full flex flex-col">
          <div className="flex justify-between items-center mb-10">
            <Link
              to="/"
              onClick={() => {
                setIsMenuOpen(false);
                if (location.pathname === '/') {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="flex items-center"
            >
              <Logo variant="mobile" />
            </Link>
            <button
              onClick={() => setIsMenuOpen(false)}
              className="text-white p-2"
            >
              <X size={24} />
            </button>
          </div>
          <nav className="flex flex-col space-y-6 flex-1">
            {navItems.map((link, i) => (
              <button
                key={i}
                onClick={() => {
                  scrollToSection(link.id)
                  setIsMenuOpen(false)
                }}
                className="text-white text-2xl font-medium hover:text-gray-400 transition-colors py-2 text-left border-b border-white/10 flex items-center justify-between"
              >
                {link.label}
                <ChevronDown
                  size={18}
                  className="transform -rotate-90 rtl:rotate-90"
                />
              </button>
            ))}
          </nav>
          <div className="mt-auto pt-10">
            <div className="flex flex-col space-y-4">
              <Link
                to="/login"
                className="border border-white/30 text-white px-6 py-4 rounded-xl font-medium text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('login')}
              </Link>
              <Link
                to="/signup"
                className="bg-white text-black px-6 py-4 rounded-xl font-medium flex items-center justify-center"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('signup')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}